import { useCallback, useContext, useMemo } from 'react';
import * as THREE from 'three';
import { exportDxf, DxfBlock, DxfEntity, DxfTextEntity } from '@pointorama/dxf-exporter';
import { isAnnotationContextGroup } from '../../contexts/AnnotationContext';
import { useAnnotationTree } from './useAnnotationContext';
import { RendererContext } from '../../contexts/RendererContext';
import { useUnitPreference } from '../../modules/Properties/integration/UserSettingsIntegration';
import { getWallSketchSkinning2DOutlineColorProperty } from '../../modules/Properties/integration/annotations/WallSketchProperties';
import {
  getAnnotationColorProperty,
  getPointPositionProperty,
  getDistancePointsProperty,
  getAreaPointsProperty,
  getRoomPolygonProperty,
  getOutlineOuterRingProperty,
  getOutlineCavitiesProperty,
  getStructurePolygonProperty,
  getStructureKindProperty,
} from '@pointorama/potree-shared';
import { StructureKind } from 'shared-types';
import { Annotation, MeasurementUnits } from '../../types/graphqlTypes';
import { hexToColor } from '../../utils/colorUtils';
import { useCondMeterToFeet, ConvertType } from '../useFormatNumber';
import { useFlatAnnotationList } from './useFlatAnnotationList';
import { useResolveFloorGroup } from './useResolveFloorGroup';
import { computeOpeningRectangle } from './openingDxfGeometry';
import { dxfBulgeFromInternal } from '../../potree/utils/skeleton/arcGeometry';
import {
  getOpeningRoomIdsProperty,
  getRoomBaseProperty,
  getFloorPlanePositionProperty,
  hasFloorPlanePosition,
  getSketchObjectAnchorProperty,
  getSketchObjectColorProperty,
  getSketchObjectDimensionDecimalsProperty,
  getSketchObjectDimensionTextSizeProperty,
  getWallSketchNodesProperty,
  getWallSketchEdgesProperty,
} from '@pointorama/potree-shared';
import { extractOutlineRings, flattenRingComponents, resolveOutlineRingsFromAnnotations, type OutlineRingComponent } from '../../potree/utils/skeleton/outlineRings';
import { SketchObjectDxfBlockRegistry, sketchObjectToDxfInsert } from './sketchObjectDxfBlocks';
import { placedDimensionOffset } from '../../potree/sketchObjects/custom/customSketchObjectType';
import { OpeningDxfBlockRegistry, openingToDxfEntities } from './openingDxfBlocks';

export enum DxfExportTypes {
  'POINT',
  'DISTANCE',
  'AREA',
  'ROOM',
  'OUTLINE',
  'STRUCTURE',
}

// Layer suffix per structure subtype, following the "<floor>_<Type>" convention.
const STRUCTURE_LAYER_SUFFIX: Record<StructureKind, string> = {
  [StructureKind.BEAM]: 'Beams',
  [StructureKind.COLUMN]: 'Columns',
};

type PointConverter = (point: { x: number; y: number; z: number }) => { x: number; y: number; z: number };

// Compose a layer name from an optional floor (group) name and a type suffix.
// With a floor: "<Floorname>_<suffix>"; without one the suffix stands alone.
function floorLayer(groupName: string | undefined, suffix: string): string {
  return groupName ? `${groupName}_${suffix}` : suffix;
}

function annotationToDxfEntity(annotation: Annotation, groupName?: string, convertPoint?: PointConverter): DxfEntity | null {
  const cp = convertPoint ?? ((p) => p);
  const colorHex = getAnnotationColorProperty(annotation.identifier).value as string;
  const color = colorHex ? hexToColor(colorHex) : undefined;

  switch (annotation.type) {
    case 'POINT': {
      const position = getPointPositionProperty(annotation.identifier).value;
      return {
        type: 'POINT',
        position: cp(position),
        color,
        layerName: floorLayer(groupName, 'Points'),
      };
    }
    case 'DISTANCE': {
      const points = getDistancePointsProperty(annotation.identifier).value;
      return {
        type: 'POLYLINE',
        points: points.map((p) => cp(p.position)),
        closed: false,
        color,
        layerName: floorLayer(groupName, 'Polylines'),
      };
    }
    case 'AREA': {
      const points = getAreaPointsProperty(annotation.identifier).value;
      return {
        type: 'POLYLINE',
        points: points.map((p) => cp(p.position)),
        closed: true,
        color,
        layerName: floorLayer(groupName, 'Polygons'),
      };
    }
    case 'ROOM': {
      const polygon = getRoomPolygonProperty(annotation.identifier).value;
      return {
        type: 'POLYLINE',
        points: polygon.map(cp),
        closed: true,
        color,
        layerName: floorLayer(groupName, 'Rooms'),
      };
    }
    case 'STRUCTURE': {
      const polygon = getStructurePolygonProperty(annotation.identifier).value;
      const kind = getStructureKindProperty(annotation.identifier).value;
      return {
        type: 'POLYLINE',
        points: polygon.map(cp),
        closed: true,
        dashed: true,
        color,
        layerName: floorLayer(groupName, STRUCTURE_LAYER_SUFFIX[kind]),
      };
    }
    default:
      return null;
  }
}

function openingToDxfEntity(opening: Annotation, groupName: string | undefined, convertPoint: PointConverter, outlineRings: { x: number; y: number }[][] | undefined): DxfEntity | null {
  const cp = convertPoint;
  const colorHex = getAnnotationColorProperty(opening.identifier).value as string;
  const color = colorHex ? hexToColor(colorHex) : undefined;
  const layerName = floorLayer(groupName, 'Openings');

  const result = computeOpeningRectangle(opening.identifier, outlineRings);
  if (result.fallback) {
    console.warn(`[DXF] Opening ${opening.identifier} falling back to raw polygon: ${result.reason}`);
    const points = getAreaPointsProperty(opening.identifier).value;
    if (points.length === 0) return null;
    const roomIds = getOpeningRoomIdsProperty(opening.identifier).value;
    const flatZ = roomIds.length > 0 ? getRoomBaseProperty(roomIds[0]).value.position.z : Math.min(...points.map((p) => p.position.z));
    return {
      type: 'POLYLINE',
      points: points.map((p) => cp({ x: p.position.x, y: p.position.y, z: flatZ })),
      closed: true,
      color,
      layerName,
    };
  }
  return {
    type: 'POLYLINE',
    points: result.corners.map(cp),
    closed: true,
    color,
    layerName,
  };
}

function outlineToDxfEntities(outline: Annotation, groupName: string | undefined, convertPoint: PointConverter): DxfEntity[] {
  const colorHex = getAnnotationColorProperty(outline.identifier).value as string;
  const color = colorHex ? hexToColor(colorHex) : undefined;
  const layerName = floorLayer(groupName, 'Outlines');

  // Outer ring plus each cavity as separate closed polylines.
  const rings: { x: number; y: number; z: number }[][] = [];
  const outer = getOutlineOuterRingProperty(outline.identifier).value;
  if (outer && outer.length >= 3) rings.push(outer);
  for (const cav of getOutlineCavitiesProperty(outline.identifier).value ?? []) {
    if (cav.length >= 3) rings.push(cav);
  }

  return rings.map((ring) => ({
    type: 'POLYLINE' as const,
    points: ring.map(convertPoint),
    closed: true,
    color,
    layerName,
  }));
}

/** Merged-outline variant of {@link outlineToDxfEntities}: the floor's
 *  `role:'OUTLINE'` ring components (already tessellated), colored from the
 *  carrying annotation (the wall sketch, or the superseded tombstone). */
function mergedOutlineToDxfEntities(annotation: Annotation, components: readonly OutlineRingComponent[], groupName: string | undefined, convertPoint: PointConverter): DxfEntity[] {
  const colorHex = getAnnotationColorProperty(annotation.identifier).value as string;
  const color = colorHex ? hexToColor(colorHex) : undefined;
  const layerName = floorLayer(groupName, 'Outlines');
  return flattenRingComponents(components)
    .filter((ring) => ring.length >= 3)
    .map((ring) => ({
      type: 'POLYLINE' as const,
      points: ring.map(convertPoint),
      closed: true,
      color,
      layerName,
    }));
}

// The floor (group) elevation in model units, or null when the annotation isn't
// on a floor that has a defined plane — those objects keep their own Z.
function floorElevation(groupIdentifier: string | null | undefined): number | null {
  if (!groupIdentifier) return null;
  try {
    if (!hasFloorPlanePosition(groupIdentifier)) return null;
    const z = getFloorPlanePositionProperty(groupIdentifier).value?.z;
    return typeof z === 'number' ? z : null;
  } catch {
    return null;
  }
}

// Flatten every coordinate of these entities to a single elevation. The DXF is a
// 2D plan, so all objects on a floor are projected onto that floor's plane.
function flattenEntitiesToZ(entities: DxfEntity[], z: number): void {
  for (const entity of entities) {
    switch (entity.type) {
      case 'POINT':
      case 'TEXT':
        entity.position.z = z;
        break;
      case 'POLYLINE':
        for (const p of entity.points) p.z = z;
        break;
      case 'HATCH':
        for (const p of entity.outerBoundary) p.z = z;
        for (const island of entity.islands) for (const p of island) p.z = z;
        break;
      case 'LINE':
        entity.start.z = z;
        entity.end.z = z;
        break;
      // Block geometry stays at z 0 — the insertion point carries the elevation.
      case 'INSERT':
        entity.position.z = z;
        break;
    }
  }
}

// Black text, viewed on the usual white CAD canvas.
const LABEL_TEXT_COLOR = { r: 0, g: 0, b: 0 };
// Base text height in METERS (model units) — converted to the active drawing
// unit per export so text keeps a constant physical size whether the DXF is in
// meters or feet (otherwise feet drawings render the text ~3.3× too small).
const LABEL_TEXT_HEIGHT_METERS = 0.15;

// A live measure label sprite: its visibility flag and world position. The
// value text is no longer read from the sprite — the exporter computes it from
// geometry / measure getters so it can't drift from the display formatting.
type MeasureLabel = { visible: boolean; getWorldPosition: (target: THREE.Vector3) => THREE.Vector3 };

// Decimal places per value kind, mirroring the on-screen labels.
const LENGTH_DECIMALS = 2;
const AREA_DECIMALS = 1;
const VOLUME_DECIMALS = 1;

// Format a numeric value (already in drawing units) as bare text — no unit, as
// the DXF $INSUNITS header declares the drawing unit.
function formatValue(value: number, decimals: number): string {
  return value.toFixed(decimals);
}

// Build TEXT entities anchored at a label sprite's world position, with the
// caller-supplied value text. Text may span multiple lines (e.g. a window's
// height + sill); DXF TEXT is single-line, so each line becomes its own entity
// stacked downwards.
function spriteLabelDxfEntities(label: MeasureLabel | undefined, text: string, layerName: string, convertPoint: PointConverter, textHeight: number): DxfTextEntity[] {
  if (!label?.visible || !text) return [];
  const world = label.getWorldPosition(new THREE.Vector3());
  if (!Number.isFinite(world.x) || !Number.isFinite(world.y) || !Number.isFinite(world.z)) return [];

  const base = convertPoint({ x: world.x, y: world.y, z: world.z });
  return text.split('\n').map((line, i) => ({
    type: 'TEXT' as const,
    position: { x: base.x, y: base.y - i * textHeight * 1.5, z: base.z },
    text: line,
    height: textHeight,
    color: LABEL_TEXT_COLOR,
    layerName,
  }));
}

// Build aligned TEXT entities for the segment-length labels of a polyline.
// Each label is centered on its segment, rotated to run along it, and flipped
// 180° when it would read upside down. It is then offset perpendicular: for a
// closed shape (room/area/opening) toward the interior, unless `outward` is set
// (structures read better with labels outside the footprint); for an open
// polyline (distance) to the readable "up" side so it sits just off the line.
// Both the position and the length value come from the vertices; the sprite is
// consulted only for visibility (whether the user has that label toggled on).
function edgeLabelDxfEntities(
  polygonRaw: { x: number; y: number; z: number }[],
  edgeLabels: MeasureLabel[],
  closed: boolean,
  layerName: string,
  convertPoint: PointConverter,
  textHeight: number,
  outward = false,
  decimals = LENGTH_DECIMALS,
  valueScale = 1,
): DxfTextEntity[] {
  // How far (in drawing units) to push the label off the line it labels.
  const edgeInset = textHeight;
  const poly = polygonRaw.map(convertPoint);
  const n = poly.length;
  if (n < 2) return [];

  const segmentCount = closed ? n : n - 1;

  // For a closed shape, the signed area gives the winding so we can pick the
  // inward normal: interior is left of a CCW edge, right of a CW edge.
  let ccw = true;
  if (closed) {
    let area2 = 0;
    for (let i = 0; i < n; i++) {
      const a = poly[i];
      const b = poly[(i + 1) % n];
      area2 += a.x * b.y - b.x * a.y;
    }
    ccw = area2 > 0;
  }

  const entities: DxfTextEntity[] = [];
  for (let i = 0; i < edgeLabels.length && i < segmentCount; i++) {
    const label = edgeLabels[i];
    if (!label?.visible) continue;

    const a = poly[i];
    const b = poly[(i + 1) % n];
    const ex = b.x - a.x;
    const ey = b.y - a.y;
    const len2d = Math.hypot(ex, ey);
    if (len2d < 1e-9) continue;
    const dx = ex / len2d;
    const dy = ey / len2d;

    // The label value is the true (3D) segment length, in drawing units.
    const length = Math.hypot(ex, ey, b.z - a.z);

    // Align with the segment, then flip into the readable half-turn (-90°, 90°].
    let angle = Math.atan2(dy, dx);
    if (angle > Math.PI / 2) angle -= Math.PI;
    else if (angle <= -Math.PI / 2) angle += Math.PI;

    // Offset normal. Closed: inward (winding-based), or outward when requested.
    // Open: the text's "up" direction (perpendicular to the readable angle), so
    // the label always sits on a consistent, readable side just off the line.
    const inwardSign = outward ? -1 : 1;
    const nx = closed ? inwardSign * (ccw ? -dy : dy) : -Math.sin(angle);
    const ny = closed ? inwardSign * (ccw ? dx : -dx) : Math.cos(angle);

    const position = {
      x: (a.x + b.x) / 2 + nx * edgeInset,
      y: (a.y + b.y) / 2 + ny * edgeInset,
      z: (a.z + b.z) / 2,
    };

    entities.push({
      type: 'TEXT',
      position,
      text: formatValue(length * valueScale, decimals),
      height: textHeight,
      rotation: (angle * 180) / Math.PI,
      hAlign: 1, // center
      vAlign: 2, // middle
      color: LABEL_TEXT_COLOR,
      layerName,
    });
  }
  return entities;
}

// The opening's plan frame: openings are vertical rectangles, so in plan they
// collapse to a line — their longest horizontal (XY) edge, the width. Returns
// the centered anchor, the width length, the readable rotation, and the unit
// perpendicular pointing to the readable "up" side. Null when the opening has
// no measurable horizontal extent.
type OpeningFrame = { center: { x: number; y: number; z: number }; length: number; angle: number; nx: number; ny: number };
function openingFrame(poly: { x: number; y: number; z: number }[]): OpeningFrame | null {
  const n = poly.length;
  if (n < 2) return null;

  let best: { a: (typeof poly)[number]; b: (typeof poly)[number]; ex: number; ey: number; len: number } | null = null;
  for (let i = 0; i < n; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % n];
    const ex = b.x - a.x;
    const ey = b.y - a.y;
    const len = Math.hypot(ex, ey);
    if (!best || len > best.len) best = { a, b, ex, ey, len };
  }
  if (!best || best.len < 1e-9) return null;

  // Align with the width, flipped into the readable half-turn (-90°, 90°].
  let angle = Math.atan2(best.ey / best.len, best.ex / best.len);
  if (angle > Math.PI / 2) angle -= Math.PI;
  else if (angle <= -Math.PI / 2) angle += Math.PI;

  return {
    center: { x: (best.a.x + best.b.x) / 2, y: (best.a.y + best.b.y) / 2, z: (best.a.z + best.b.z) / 2 },
    length: best.len,
    angle,
    nx: -Math.sin(angle),
    ny: Math.cos(angle),
  };
}

// Width label for an opening: a single value centered on the opening line (the
// two horizontal edges carry the same value; the vertical edges duplicate the
// height label). Emitted only when edge/distance labels are on.
function openingWidthLabelDxfEntities(frame: OpeningFrame, edgeLabels: MeasureLabel[], layerName: string, textHeight: number, valueScale = 1): DxfTextEntity[] {
  if (!edgeLabels.some((label) => label?.visible)) return [];
  return [
    {
      type: 'TEXT',
      position: { ...frame.center },
      text: formatValue(frame.length * valueScale, LENGTH_DECIMALS),
      height: textHeight,
      rotation: (frame.angle * 180) / Math.PI,
      hAlign: 1, // center
      vAlign: 2, // middle
      color: LABEL_TEXT_COLOR,
      layerName,
    },
  ];
}

// Height label(s) for an opening: stacked just off the opening line on the
// readable side, aligned with the opening, clear of the width text. Windows
// carry two lines (height, then sill); doors a single height.
function openingHeightLabelDxfEntities(frame: OpeningFrame, heightLabel: MeasureLabel | undefined, values: string[], layerName: string, textHeight: number): DxfTextEntity[] {
  if (!heightLabel?.visible || values.length === 0) return [];
  // Clearance from the opening line to the first height label, enough to clear
  // the width text that sits on the line.
  const clearance = textHeight * 2;
  return values.map((text, i) => {
    const offset = clearance + i * textHeight * 1.5;
    return {
      type: 'TEXT' as const,
      position: {
        x: frame.center.x + frame.nx * offset,
        y: frame.center.y + frame.ny * offset,
        z: frame.center.z,
      },
      text,
      height: textHeight,
      rotation: (frame.angle * 180) / Math.PI,
      hAlign: 1, // center
      vAlign: 2, // middle
      color: LABEL_TEXT_COLOR,
      layerName,
    };
  });
}

// Converts a model-unit scalar to drawing units (e.g. m² → ft²) for the active
// unit preference. ConvertType selects the power (DISTANCE/AREA/VOLUME).
type ScalarConverter = (value: number, convertType: ConvertType) => number;

/**
 * How a build writes its numbers. A DXF file is drawn in the user's unit (and
 * declares it in $INSUNITS); the print engine always draws in meters while
 * label VALUES still read in the user's unit.
 */
interface DrawingUnits {
  /** Model meters → drawing units (geometry). */
  point: PointConverter;
  /** Model meters → drawing units (text heights, elevations). */
  scalar: ScalarConverter;
  /** Model meters → the user's display unit (label values). */
  display: ScalarConverter;
  /** Drawing-unit length → display-unit length. */
  lengthValueScale: number;
}

const identityPoint: PointConverter = (point) => ({ x: point.x, y: point.y, z: point.z });
const identityScalar: ScalarConverter = (value) => value;

/** Options of the plan builders; omitted = the DXF export's behavior. */
export interface DxfBuildOptions {
  /** Draw in meters whatever the unit preference (print). Label values still follow the preference. */
  metric?: boolean;
  /** World text heights (meters) labels are placed around — the print engine passes the paper size × scale. */
  textHeightMeters?: Partial<Record<'labels' | 'dimensions' | 'roomTags', number>>;
  /**
   * Room tags as ONE multi-line text (print): the print engine stacks the lines on the page.
   * The DXF export writes one TEXT per line, stacked along world Y (DXF TEXT has no line breaks) —
   * which would put the lines side by side once a print view is turned.
   */
  multilineTags?: boolean;
}

// The live scene object that owns an annotation's label sprites and the getters
// for their values. Measures (point/distance/area/opening) live in
// scene.measurements; rooms live in scene.rooms and expose a slightly different
// shape (singular volumeLabel + getVolume, no coordinate/height labels). Both
// are read structurally here. Values are pulled from geometry / getters, never
// from the sprite text.
type LabeledSceneObject = {
  coordinateLabels?: MeasureLabel[];
  edgeLabels?: MeasureLabel[];
  areaLabel?: MeasureLabel;
  heightLabel?: MeasureLabel;
  volumeLabels?: MeasureLabel[];
  volumeLabel?: MeasureLabel;
  getPolygon?: () => { x: number; y: number; z: number }[];
  points?: { position: { x: number; y: number; z: number } }[];
  getArea?: () => number;
  getVolume?: () => number;
  volumes?: number[];
  getHeightValues?: () => number[];
};

// Collect TEXT entities for every currently-visible value label of an
// annotation. Positions come from the live label sprites; values come from
// geometry / measure getters (never parsed from the sprite text). Annotations
// without label sprites contribute none.
function collectLabelDxfEntities(annotation: Annotation, groupName: string | undefined, viewer: typeof window.viewer | undefined, units: DrawingUnits, textHeightMeters: number): DxfTextEntity[] {
  const measure = (viewer?.scene?.measurements?.find((m) => m.identifier === annotation.identifier) ??
    viewer?.scene?.rooms?.find((r) => r.identifier === annotation.identifier) ??
    viewer?.scene?.structures?.find((s) => s.identifier === annotation.identifier)) as LabeledSceneObject | undefined;
  if (!measure) return [];

  const { point: convertPoint, display } = units;
  // Text height in the active drawing unit, so labels keep a constant physical
  // size in both meter and feet exports.
  const textHeight = units.scalar(textHeightMeters, ConvertType.DISTANCE);

  // Each label kind lands on its own "<Floorname>_Labels_<Kind>" layer.
  const labelLayer = (kind: string) => floorLayer(groupName, `Labels_${kind}`);
  const entities: DxfTextEntity[] = [];
  const pushSprite = (label: MeasureLabel | undefined, text: string, layer: string) => {
    entities.push(...spriteLabelDxfEntities(label, text, layer, convertPoint, textHeight));
  };

  // Coordinate labels: one per vertex, value = the vertex in drawing units.
  const points = measure.points;
  if (points) {
    const coordinateLabels = measure.coordinateLabels ?? [];
    for (let i = 0; i < coordinateLabels.length && i < points.length; i++) {
      const p = points[i].position;
      const text = [p.x, p.y, p.z].map((v) => formatValue(display(v, ConvertType.DISTANCE), LENGTH_DECIMALS)).join(' / ');
      pushSprite(coordinateLabels[i], text, labelLayer('Coordinate'));
    }
  }

  const polygon = measure.getPolygon?.() ?? points?.map((p) => p.position);
  const heightValues = (measure.getHeightValues?.() ?? []).map((v) => formatValue(display(v, ConvertType.DISTANCE), LENGTH_DECIMALS));

  if (annotation.type === 'OPENING') {
    // An opening collapses to a line in plan: width centered on the line, with
    // the height (and a window's sill) stacked just off it.
    const frame = polygon ? openingFrame(polygon.map(convertPoint)) : null;
    if (frame) {
      // Openings collapse to a line, so their "length" label reads as Width.
      if (measure.edgeLabels) entities.push(...openingWidthLabelDxfEntities(frame, measure.edgeLabels, labelLayer('Width'), textHeight, units.lengthValueScale));
      entities.push(...openingHeightLabelDxfEntities(frame, measure.heightLabel, heightValues, labelLayer('Height'), textHeight));
    }
  } else {
    // Segment-length labels: placed and valued from the geometry. Closed shapes
    // (room/area) offset inward; structures outward (clearer outside the small
    // footprint); open polylines (distance) to a readable side.
    if (polygon != null && measure.edgeLabels) {
      entities.push(...edgeLabelDxfEntities(polygon, measure.edgeLabels, annotation.type !== 'DISTANCE', labelLayer('Length'), convertPoint, textHeight, annotation.type === 'STRUCTURE', LENGTH_DECIMALS, units.lengthValueScale));
    }

    if (measure.getArea) pushSprite(measure.areaLabel, formatValue(display(measure.getArea(), ConvertType.AREA), AREA_DECIMALS), labelLayer('Area'));

    // Height label sprite, value(s) sourced from the measure getter.
    if (measure.getHeightValues) pushSprite(measure.heightLabel, heightValues.join('\n'), labelLayer('Height'));
  }

  // Volume: measures carry one value per sub-volume; rooms expose a single one.
  if (measure.volumes && measure.volumeLabels) {
    for (let i = 0; i < measure.volumes.length && i < measure.volumeLabels.length; i++) {
      pushSprite(measure.volumeLabels[i], formatValue(display(measure.volumes[i], ConvertType.VOLUME), VOLUME_DECIMALS), labelLayer('Volume'));
    }
  }
  if (measure.getVolume) pushSprite(measure.volumeLabel, formatValue(display(measure.getVolume(), ConvertType.VOLUME), VOLUME_DECIMALS), labelLayer('Volume'));

  return entities;
}

function downloadDxf(content: string, filename = 'export.dxf') {
  const blob = new Blob([content], { type: 'application/dxf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

// Group (floor/surface) display names by identifier — used for layer names.
const useGroupNameByIdentifier = (): Record<string, string> => {
  const annotationTree = useAnnotationTree();
  return useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of annotationTree) {
      if (isAnnotationContextGroup(item)) {
        map[item.identifier] = item.name;
      }
    }
    return map;
  }, [annotationTree]);
};

// Unit handling shared by the DXF exports: $INSUNITS (6 = Meters, 2 = Feet) and
// the meters→drawing-units point converter.
const useDxfUnits = () => {
  const { unit: measurementUnit } = useUnitPreference();
  const { condMeterToFeet } = useCondMeterToFeet();

  const insUnits = measurementUnit === MeasurementUnits.Feet ? 2 : 6;

  const convertPoint: PointConverter = useCallback(
    (point) => ({
      x: condMeterToFeet(point.x, ConvertType.DISTANCE),
      y: condMeterToFeet(point.y, ConvertType.DISTANCE),
      z: condMeterToFeet(point.z, ConvertType.DISTANCE),
    }),
    [condMeterToFeet],
  );

  // The DXF draws in the preference unit; `metric` (print) draws in meters, values still in the preference.
  const drawingUnits = useCallback(
    (metric = false): DrawingUnits =>
      metric
        ? { point: identityPoint, scalar: identityScalar, display: condMeterToFeet, lengthValueScale: condMeterToFeet(1, ConvertType.DISTANCE) }
        : { point: convertPoint, scalar: condMeterToFeet, display: condMeterToFeet, lengthValueScale: 1 },
    [convertPoint, condMeterToFeet],
  );

  return { insUnits, drawingUnits };
};

export const useExportDXF = () => {
  const rendererContext = useContext(RendererContext);
  const { allAnnotations } = useFlatAnnotationList();
  const { insUnits, drawingUnits } = useDxfUnits();
  const groupNameByIdentifier = useGroupNameByIdentifier();
  // Sketches filed in a room folder carry the folder id — resolve to the floor
  // for elevations and layer names.
  const resolveFloorGroup = useResolveFloorGroup();

  const isExportDXFPossible = useCallback(
    (annotations?: Annotation[]): boolean => {
      const list = annotations ?? allAnnotations;
      return list.some((annotation) => annotation?.type in DxfExportTypes);
    },
    [allAnnotations],
  );

  // Build only; the floors export merges this with the layout entities.
  const buildObjectsDXF = useCallback(
    (annotations?: Annotation[], opts?: { includeLabels?: boolean } & DxfBuildOptions): DxfEntity[] => {
      const list = annotations ?? allAnnotations;
      const units = drawingUnits(opts?.metric);
      const convertPoint = units.point;
      const labelTextHeight = opts?.textHeightMeters?.labels ?? LABEL_TEXT_HEIGHT_METERS;

      // Index openings (across all annotations, not just the export subset) by
      // the room ids they link to. An opening only appears in the DXF if one
      // of its linked rooms is in `list`; emitted once even if both rooms are
      // exported.
      const openingsByRoomId: Record<string, Annotation[]> = {};
      for (const annotation of allAnnotations) {
        if (annotation.type !== 'OPENING') continue;
        const roomIds = getOpeningRoomIdsProperty(annotation.identifier).value;
        for (const roomId of roomIds) {
          (openingsByRoomId[roomId] ??= []).push(annotation);
        }
      }

      // Per-floor outline rings, resolved merged-first (the wall sketch's
      // `role:'OUTLINE'` cycles) with the legacy OUTLINE annotations as
      // fallback. Openings snap their outer edge to these; the merged rings
      // also decide whether a legacy OUTLINE annotation is a superseded
      // tombstone (skipped) or still the authoritative outline.
      const mergedRingsByFloor = new Map<string, OutlineRingComponent[]>();
      const mergedRingsForFloor = (floorId: string | null | undefined): OutlineRingComponent[] => {
        if (!floorId) return [];
        let merged = mergedRingsByFloor.get(floorId);
        if (merged === undefined) {
          merged = [];
          const sketch = allAnnotations.find((a) => a.type === 'WALL_SKETCH' && a.groupIdentifier === floorId);
          if (sketch) {
            try {
              merged = extractOutlineRings(getWallSketchNodesProperty(sketch.identifier).value, getWallSketchEdgesProperty(sketch.identifier).value);
            } catch {
              merged = [];
            }
          }
          mergedRingsByFloor.set(floorId, merged);
        }
        return merged;
      };
      const snapRingsByFloor = new Map<string, { x: number; y: number; z: number }[][]>();
      const snapRingsForFloor = (floorId: string | null | undefined): { x: number; y: number; z: number }[][] | undefined => {
        if (!floorId) return undefined;
        let rings = snapRingsByFloor.get(floorId);
        if (rings === undefined) {
          rings = flattenRingComponents(resolveOutlineRingsFromAnnotations(allAnnotations, floorId));
          snapRingsByFloor.set(floorId, rings);
        }
        return rings;
      };
      /** Floors whose merged outline already went out (one emission per floor,
       *  whether the wall sketch or a tombstone row triggered it). */
      const emittedMergedOutlineFloors = new Set<string>();

      const entities: DxfEntity[] = [];
      const emittedOpenings = new Set<string>();

      // Collect an annotation's entities, project them onto its floor plane (DXF
      // is 2D), then add them to the output. Objects not on a floor keep their Z.
      const emit = (groupIdentifier: string | null | undefined, built: DxfEntity[]) => {
        const elevation = floorElevation(groupIdentifier);
        if (elevation != null) flattenEntitiesToZ(built, units.scalar(elevation, ConvertType.DISTANCE));
        entities.push(...built);
      };

      for (const annotation of list) {
        const floorGroupId = resolveFloorGroup(annotation.groupIdentifier);
        const groupName = floorGroupId ? groupNameByIdentifier[floorGroupId] : undefined;

        if (annotation.type === 'WALL_SKETCH') {
          // The merged outline exports through its carrying sketch; the wall
          // skin itself has its own export section below.
          const merged = mergedRingsForFloor(floorGroupId);
          if (merged.length > 0 && floorGroupId && !emittedMergedOutlineFloors.has(floorGroupId)) {
            emittedMergedOutlineFloors.add(floorGroupId);
            emit(floorGroupId, mergedOutlineToDxfEntities(annotation, merged, groupName, convertPoint));
          }
          continue;
        }

        if (annotation.type === 'OUTLINE') {
          const merged = mergedRingsForFloor(floorGroupId);
          if (merged.length > 0) {
            // Superseded tombstone: the merged rings are authoritative. Emit
            // them once (covers exports where the sketch row isn't in `list`).
            if (floorGroupId && !emittedMergedOutlineFloors.has(floorGroupId)) {
              emittedMergedOutlineFloors.add(floorGroupId);
              emit(floorGroupId, mergedOutlineToDxfEntities(annotation, merged, groupName, convertPoint));
            }
            continue;
          }
          emit(floorGroupId, outlineToDxfEntities(annotation, groupName, convertPoint));
          continue;
        }

        const built: DxfEntity[] = [];
        const entity = annotationToDxfEntity(annotation, groupName, convertPoint);
        if (entity) built.push(entity);
        if (opts?.includeLabels) {
          built.push(...collectLabelDxfEntities(annotation, groupName, rendererContext.viewer, units, labelTextHeight));
        }
        emit(floorGroupId, built);

        if (annotation.type === 'ROOM') {
          const linked = openingsByRoomId[annotation.identifier] ?? [];
          for (const opening of linked) {
            if (emittedOpenings.has(opening.identifier)) continue;
            emittedOpenings.add(opening.identifier);
            const openingFloorId = resolveFloorGroup(opening.groupIdentifier);
            const openingGroupName = openingFloorId ? groupNameByIdentifier[openingFloorId] : undefined;
            const openingBuilt: DxfEntity[] = [];
            const openingEntity = openingToDxfEntity(opening, openingGroupName, convertPoint, snapRingsForFloor(resolveFloorGroup(annotation.groupIdentifier)));
            if (openingEntity) openingBuilt.push(openingEntity);
            if (opts?.includeLabels) {
              openingBuilt.push(...collectLabelDxfEntities(opening, openingGroupName, rendererContext.viewer, units, labelTextHeight));
            }
            emit(openingFloorId, openingBuilt);
          }
        }
      }

      return entities;
    },
    [allAnnotations, groupNameByIdentifier, rendererContext.viewer, drawingUnits, resolveFloorGroup],
  );

  const onExportDXF = useCallback(
    (annotations?: Annotation[], opts?: { includeLabels?: boolean }) => {
      const entities = buildObjectsDXF(annotations, opts);
      if (entities.length > 0) downloadDxf(exportDxf(entities, { insUnits }));
    },
    [buildObjectsDXF, insUnits],
  );

  return [onExportDXF, isExportDXFPossible, buildObjectsDXF] as const;
};

// Flat [x1,y1,z1, x2,y2,z2, ...] glyph segments → one open 2-point polyline each.
function segmentsToPolylines(segments: readonly number[], color: { r: number; g: number; b: number } | undefined, layerName: string, cp: PointConverter): DxfEntity[] {
  const out: DxfEntity[] = [];
  for (let i = 0; i + 5 < segments.length; i += 6) {
    out.push({
      type: 'POLYLINE',
      points: [cp({ x: segments[i], y: segments[i + 1], z: segments[i + 2] }), cp({ x: segments[i + 3], y: segments[i + 4], z: segments[i + 5] })],
      closed: false,
      color,
      layerName,
    });
  }
  return out;
}

// A room tag's lines (name, area) stacked and centered on the tag anchor.
function roomTagDxfEntities(tag: { position: { x: number; y: number; z: number }; text: string }, layerName: string, convertPoint: PointConverter, textHeight: number, multiline = false): DxfTextEntity[] {
  const lines = tag.text.split('\n').filter(Boolean);
  const anchor = convertPoint(tag.position);
  if (multiline) return [{ type: 'TEXT', position: anchor, text: lines.join('\n'), height: textHeight, hAlign: 1, vAlign: 2, color: LABEL_TEXT_COLOR, layerName }];
  const pitch = textHeight * 1.5;
  return lines.map((line, i) => ({
    type: 'TEXT' as const,
    position: { x: anchor.x, y: anchor.y + ((lines.length - 1) / 2 - i) * pitch, z: anchor.z },
    text: line,
    height: textHeight,
    hAlign: 1, // center
    vAlign: 2, // middle
    color: LABEL_TEXT_COLOR,
    layerName,
  }));
}

// Layout persona export: whole floors, DXF only. Per floor: the wall footprint
// outline with openings cut out (as the 2D skin renders it) on <Floor>_Walls,
// door/window glyphs on <Floor>_Openings, sketch object glyphs on <Floor>_Objects.
export const useExportLayoutDXF = () => {
  const rendererContext = useContext(RendererContext);
  const { insUnits, drawingUnits } = useDxfUnits();
  const groupNameByIdentifier = useGroupNameByIdentifier();

  // Build only; block definitions come with the entities.
  const buildLayoutDXF = useCallback(
    (floorGroupIds: string[], opts?: DxfBuildOptions & { separateDimensions?: boolean }): { entities: DxfEntity[]; blocks: DxfBlock[] } => {
      const viewer = rendererContext.viewer;
      if (!viewer) return { entities: [], blocks: [] };
      const units = drawingUnits(opts?.metric);
      const convertPoint = units.point;
      const dimensionTextHeight = opts?.textHeightMeters?.dimensions ?? LABEL_TEXT_HEIGHT_METERS;
      const roomTagTextHeight = units.scalar(opts?.textHeightMeters?.roomTags ?? LABEL_TEXT_HEIGHT_METERS, ConvertType.DISTANCE);

      const entities: DxfEntity[] = [];
      const blockRegistry = new SketchObjectDxfBlockRegistry();
      const openingBlockRegistry = new OpeningDxfBlockRegistry();
      for (const floorId of floorGroupIds) {
        const groupName = groupNameByIdentifier[floorId];
        const floorEntities: DxfEntity[] = [];
        let sketchZ: number | null = null;

        for (const wallSketch of viewer.scene.wallSketches.filter((ws) => ws.groupIdentifier === floorId)) {
          const layout2D = wallSketch.getLayoutExport2D();
          if (!layout2D) continue;
          sketchZ ??= wallSketch.getFloorHeight();
          const outlineColor = hexToColor(getWallSketchSkinning2DOutlineColorProperty(wallSketch.identifier).value);
          for (const loop of layout2D.outlineLoops) {
            const hasArcs = loop.bulges.some((b) => b !== 0);
            // A full-circle wall loop legitimately collapses to 2 bulged vertices.
            if (loop.points.length < (hasArcs ? 2 : 3)) continue;
            floorEntities.push({
              type: 'POLYLINE',
              points: loop.points.map(convertPoint),
              closed: true,
              bulges: hasArcs ? loop.bulges.map(dxfBulgeFromInternal) : undefined,
              color: outlineColor,
              layerName: floorLayer(groupName, 'Walls'),
            });
          }
          // Openings export as block INSERTs — identical openings share one
          // block definition, with door swings as true arcs inside the block.
          const openingsLayer = floorLayer(groupName, 'Openings');
          for (const opening of layout2D.openings) floorEntities.push(...openingToDxfEntities(opening, openingsLayer, outlineColor, convertPoint, openingBlockRegistry));
        }

        for (const sketchObject of viewer.scene.sketchObjects.filter((so) => so.groupIdentifier === floorId)) {
          const dimensionType = sketchObject.getType();
          // Print pens dimensions apart from objects (thin vs medium); the DXF keeps them on Objects.
          const objectsLayer = floorLayer(groupName, opts?.separateDimensions && dimensionType?.dimension ? 'Dimensions' : 'Objects');
          // Objects export as block INSERTs — identical objects share one block
          // definition. Beams (world-clipped against walls) and unresolvable
          // objects fall back to loose polylines.
          const insert = sketchObjectToDxfInsert(sketchObject, objectsLayer, convertPoint, blockRegistry);
          if (insert) {
            floorEntities.push(insert);
            continue;
          }
          // Same dedicated color property the glyph renders with (black
          // default), NOT the generic annotation color.
          const colorHex = getSketchObjectColorProperty(sketchObject.identifier).value as string;
          const color = colorHex ? hexToColor(colorHex) : undefined;
          floorEntities.push(...segmentsToPolylines(sketchObject.getWorldGlyphSegments(), color, objectsLayer, convertPoint));
          // A dimension also writes its length, aligned with its (offset) line like the on-screen label.
          const anchor = getSketchObjectAnchorProperty(sketchObject.identifier).value;
          const frame = sketchObject.resolveFrame();
          if (dimensionType?.dimension && anchor.length >= 2 && frame) {
            const offset = placedDimensionOffset(dimensionType, sketchObject.identifier);
            const line = anchor.slice(0, 2).map((p) => ({ x: p.x + frame.norm.x * offset, y: p.y + frame.norm.y * offset, z: p.z }));
            const decimals = getSketchObjectDimensionDecimalsProperty(sketchObject.identifier).value ?? LENGTH_DECIMALS;
            const textHeight = units.scalar(dimensionTextHeight, ConvertType.DISTANCE) * (getSketchObjectDimensionTextSizeProperty(sketchObject.identifier).value ?? 1);
            floorEntities.push(...edgeLabelDxfEntities(line, [{ visible: true, getWorldPosition: (target) => target }], false, objectsLayer, convertPoint, textHeight, false, decimals, units.lengthValueScale));
          }
        }

        // Room tags: name + area as the plan shows them, one TEXT per line centered on the tag.
        for (const tag of viewer.roomLabelsLayer?.exportTags(floorId) ?? []) floorEntities.push(...roomTagDxfEntities(tag, floorLayer(groupName, 'RoomTags'), convertPoint, roomTagTextHeight, opts?.multilineTags));

        // Project onto the floor plane (DXF is a 2D plan), same as the floors export.
        const elevation = floorElevation(floorId) ?? sketchZ;
        if (elevation != null) flattenEntitiesToZ(floorEntities, units.scalar(elevation, ConvertType.DISTANCE));
        entities.push(...floorEntities);
      }

      return { entities, blocks: [...blockRegistry.getBlocks(), ...openingBlockRegistry.getBlocks()] };
    },
    [rendererContext.viewer, groupNameByIdentifier, drawingUnits],
  );

  const onExportLayoutDXF = useCallback(
    (floorGroupIds: string[]) => {
      const { entities, blocks } = buildLayoutDXF(floorGroupIds);
      if (entities.length > 0) downloadDxf(exportDxf(entities, { insUnits, blocks }));
    },
    [buildLayoutDXF, insUnits],
  );

  return { onExportLayoutDXF, buildLayoutDXF };
};

// Objects (as-built floors) + layout (standardised floors) in one file.
// Only the layout side defines blocks.
export const useExportFloorsDXF = () => {
  const [, , buildObjectsDXF] = useExportDXF();
  const { buildLayoutDXF } = useExportLayoutDXF();
  const { insUnits } = useDxfUnits();

  const onExportFloorsDXF = useCallback(
    (input: { objects: Annotation[]; includeLabels?: boolean; layoutFloorIds: string[] }) => {
      const layout = buildLayoutDXF(input.layoutFloorIds);
      const entities = [...buildObjectsDXF(input.objects, { includeLabels: input.includeLabels }), ...layout.entities];
      if (entities.length > 0) downloadDxf(exportDxf(entities, { insUnits, blocks: layout.blocks }));
    },
    [buildObjectsDXF, buildLayoutDXF, insUnits],
  );

  return { onExportFloorsDXF };
};
